/**
 * Audio-Bibliothek für das Creator Studio
 * Enthält 50+ Audio-Track-Definitionen (Musik, SFX, Ambient)
 * URLs sind Platzhalter - müssen mit echten Asset-URLs ersetzt werden
 */

import type { AudioElement } from './elementTypes';

/**
 * Basis-URL für Audio-Assets (Platzhalter)
 * In Production durch CDN-URL ersetzen
 */
const AUDIO_BASE_URL = '/assets/audio';

/**
 * Musik-Tracks: Upbeat/Energetisch
 */
const upbeatMusic: AudioElement[] = [
  {
    id: 'music-upbeat-001',
    name: 'Energetic Pop',
    type: 'audio',
    tags: ['upbeat', 'pop', 'energisch', 'motivierend', 'werbung'],
    category: 'Upbeat',
    audioUrl: `${AUDIO_BASE_URL}/music/upbeat/energetic-pop.mp3`,
    duration: 120,
    audioType: 'music',
    attribution: { artist: 'Studio Audio', source: 'Royalty Free' },
  },
  {
    id: 'music-upbeat-002',
    name: 'Happy Vibes',
    type: 'audio',
    tags: ['upbeat', 'fröhlich', 'positiv', 'lifestyle'],
    category: 'Upbeat',
    audioUrl: `${AUDIO_BASE_URL}/music/upbeat/happy-vibes.mp3`,
    duration: 90,
    audioType: 'music',
    attribution: { artist: 'Creative Sounds', source: 'Royalty Free' },
  },
  {
    id: 'music-upbeat-003',
    name: 'Summer Groove',
    type: 'audio',
    tags: ['upbeat', 'sommer', 'groove', 'dance'],
    category: 'Upbeat',
    audioUrl: `${AUDIO_BASE_URL}/music/upbeat/summer-groove.mp3`,
    duration: 135,
    audioType: 'music',
    attribution: { artist: 'Beat Factory', source: 'Royalty Free' },
  },
  {
    id: 'music-upbeat-004',
    name: 'Fitness Motivation',
    type: 'audio',
    tags: ['upbeat', 'sport', 'fitness', 'motivation', 'energie'],
    category: 'Upbeat',
    audioUrl: `${AUDIO_BASE_URL}/music/upbeat/fitness-motivation.mp3`,
    duration: 180,
    audioType: 'music',
    attribution: { artist: 'Gym Beats', source: 'Royalty Free' },
  },
  {
    id: 'music-upbeat-005',
    name: 'Tech Startup',
    type: 'audio',
    tags: ['upbeat', 'tech', 'modern', 'innovation', 'digital'],
    category: 'Upbeat',
    audioUrl: `${AUDIO_BASE_URL}/music/upbeat/tech-startup.mp3`,
    duration: 105,
    audioType: 'music',
    attribution: { artist: 'Digital Dreams', source: 'Royalty Free' },
  },
  {
    id: 'music-upbeat-006',
    name: 'Celebration Time',
    type: 'audio',
    tags: ['upbeat', 'feier', 'party', 'jubel', 'erfolg'],
    category: 'Upbeat',
    audioUrl: `${AUDIO_BASE_URL}/music/upbeat/celebration-time.mp3`,
    duration: 95,
    audioType: 'music',
    attribution: { artist: 'Party House', source: 'Royalty Free' },
  },
];

/**
 * Musik-Tracks: Chill/Entspannt
 */
const chillMusic: AudioElement[] = [
  {
    id: 'music-chill-001',
    name: 'Lo-Fi Study',
    type: 'audio',
    tags: ['chill', 'lofi', 'entspannt', 'fokus', 'studieren'],
    category: 'Chill',
    audioUrl: `${AUDIO_BASE_URL}/music/chill/lofi-study.mp3`,
    duration: 180,
    audioType: 'music',
    attribution: { artist: 'Lo-Fi Lab', source: 'Royalty Free' },
  },
  {
    id: 'music-chill-002',
    name: 'Sunset Dreams',
    type: 'audio',
    tags: ['chill', 'sonnenuntergang', 'romantisch', 'ruhig'],
    category: 'Chill',
    audioUrl: `${AUDIO_BASE_URL}/music/chill/sunset-dreams.mp3`,
    duration: 150,
    audioType: 'music',
    attribution: { artist: 'Ambient Works', source: 'Royalty Free' },
  },
  {
    id: 'music-chill-003',
    name: 'Coffee Shop',
    type: 'audio',
    tags: ['chill', 'café', 'jazz', 'gemütlich', 'akustisch'],
    category: 'Chill',
    audioUrl: `${AUDIO_BASE_URL}/music/chill/coffee-shop.mp3`,
    duration: 165,
    audioType: 'music',
    attribution: { artist: 'Jazz Café', source: 'Royalty Free' },
  },
  {
    id: 'music-chill-004',
    name: 'Ocean Breeze',
    type: 'audio',
    tags: ['chill', 'ozean', 'wellen', 'meditation', 'spa'],
    category: 'Chill',
    audioUrl: `${AUDIO_BASE_URL}/music/chill/ocean-breeze.mp3`,
    duration: 200,
    audioType: 'music',
    attribution: { artist: 'Nature Sounds', source: 'Royalty Free' },
  },
  {
    id: 'music-chill-005',
    name: 'Acoustic Morning',
    type: 'audio',
    tags: ['chill', 'akustisch', 'gitarre', 'morgen', 'frisch'],
    category: 'Chill',
    audioUrl: `${AUDIO_BASE_URL}/music/chill/acoustic-morning.mp3`,
    duration: 140,
    audioType: 'music',
    attribution: { artist: 'Guitar Dreams', source: 'Royalty Free' },
  },
  {
    id: 'music-chill-006',
    name: 'Piano Reflection',
    type: 'audio',
    tags: ['chill', 'klavier', 'piano', 'nachdenklich', 'emotional'],
    category: 'Chill',
    audioUrl: `${AUDIO_BASE_URL}/music/chill/piano-reflection.mp3`,
    duration: 175,
    audioType: 'music',
    attribution: { artist: 'Piano Keys', source: 'Royalty Free' },
  },
];

/**
 * Musik-Tracks: Corporate/Business
 */
const corporateMusic: AudioElement[] = [
  {
    id: 'music-corporate-001',
    name: 'Business Presentation',
    type: 'audio',
    tags: ['corporate', 'business', 'präsentation', 'professionell'],
    category: 'Corporate',
    audioUrl: `${AUDIO_BASE_URL}/music/corporate/business-presentation.mp3`,
    duration: 120,
    audioType: 'music',
    attribution: { artist: 'Corporate Sound', source: 'Royalty Free' },
  },
  {
    id: 'music-corporate-002',
    name: 'Innovation Hub',
    type: 'audio',
    tags: ['corporate', 'innovation', 'technologie', 'zukunft'],
    category: 'Corporate',
    audioUrl: `${AUDIO_BASE_URL}/music/corporate/innovation-hub.mp3`,
    duration: 135,
    audioType: 'music',
    attribution: { artist: 'Tech Audio', source: 'Royalty Free' },
  },
  {
    id: 'music-corporate-003',
    name: 'Success Story',
    type: 'audio',
    tags: ['corporate', 'erfolg', 'inspirierend', 'motivierend'],
    category: 'Corporate',
    audioUrl: `${AUDIO_BASE_URL}/music/corporate/success-story.mp3`,
    duration: 110,
    audioType: 'music',
    attribution: { artist: 'Inspire Media', source: 'Royalty Free' },
  },
  {
    id: 'music-corporate-004',
    name: 'Team Spirit',
    type: 'audio',
    tags: ['corporate', 'team', 'zusammenarbeit', 'positiv'],
    category: 'Corporate',
    audioUrl: `${AUDIO_BASE_URL}/music/corporate/team-spirit.mp3`,
    duration: 100,
    audioType: 'music',
    attribution: { artist: 'Unity Sounds', source: 'Royalty Free' },
  },
  {
    id: 'music-corporate-005',
    name: 'Clean Tech',
    type: 'audio',
    tags: ['corporate', 'clean', 'minimal', 'modern', 'elegant'],
    category: 'Corporate',
    audioUrl: `${AUDIO_BASE_URL}/music/corporate/clean-tech.mp3`,
    duration: 125,
    audioType: 'music',
    attribution: { artist: 'Minimal Sound', source: 'Royalty Free' },
  },
];

/**
 * Musik-Tracks: Cinematic/Episch
 */
const cinematicMusic: AudioElement[] = [
  {
    id: 'music-cinematic-001',
    name: 'Epic Trailer',
    type: 'audio',
    tags: ['cinematic', 'episch', 'trailer', 'dramatisch', 'film'],
    category: 'Cinematic',
    audioUrl: `${AUDIO_BASE_URL}/music/cinematic/epic-trailer.mp3`,
    duration: 90,
    audioType: 'music',
    attribution: { artist: 'Epic Score', source: 'Royalty Free' },
  },
  {
    id: 'music-cinematic-002',
    name: 'Emotional Journey',
    type: 'audio',
    tags: ['cinematic', 'emotional', 'reise', 'orchester', 'bewegend'],
    category: 'Cinematic',
    audioUrl: `${AUDIO_BASE_URL}/music/cinematic/emotional-journey.mp3`,
    duration: 180,
    audioType: 'music',
    attribution: { artist: 'Film Orchestra', source: 'Royalty Free' },
  },
  {
    id: 'music-cinematic-003',
    name: 'Action Sequence',
    type: 'audio',
    tags: ['cinematic', 'action', 'spannung', 'intensiv', 'schnell'],
    category: 'Cinematic',
    audioUrl: `${AUDIO_BASE_URL}/music/cinematic/action-sequence.mp3`,
    duration: 75,
    audioType: 'music',
    attribution: { artist: 'Action Beats', source: 'Royalty Free' },
  },
  {
    id: 'music-cinematic-004',
    name: 'Documentary Score',
    type: 'audio',
    tags: ['cinematic', 'dokumentation', 'reportage', 'seriös', 'neutral'],
    category: 'Cinematic',
    audioUrl: `${AUDIO_BASE_URL}/music/cinematic/documentary-score.mp3`,
    duration: 150,
    audioType: 'music',
    attribution: { artist: 'Doc Films', source: 'Royalty Free' },
  },
  {
    id: 'music-cinematic-005',
    name: 'Wonder & Discovery',
    type: 'audio',
    tags: ['cinematic', 'wunder', 'entdeckung', 'magisch', 'kindlich'],
    category: 'Cinematic',
    audioUrl: `${AUDIO_BASE_URL}/music/cinematic/wonder-discovery.mp3`,
    duration: 130,
    audioType: 'music',
    attribution: { artist: 'Dream Score', source: 'Royalty Free' },
  },
];

/**
 * Sound Effects: Benachrichtigungen
 */
const notificationSfx: AudioElement[] = [
  {
    id: 'sfx-notification-001',
    name: 'Ding',
    type: 'audio',
    tags: ['sfx', 'benachrichtigung', 'ding', 'nachricht', 'alert'],
    category: 'Benachrichtigung',
    audioUrl: `${AUDIO_BASE_URL}/sfx/notification/ding.mp3`,
    duration: 1,
    audioType: 'sfx',
    attribution: { artist: 'SFX Library', source: 'Royalty Free' },
  },
  {
    id: 'sfx-notification-002',
    name: 'Pop',
    type: 'audio',
    tags: ['sfx', 'benachrichtigung', 'pop', 'bubble', 'soft'],
    category: 'Benachrichtigung',
    audioUrl: `${AUDIO_BASE_URL}/sfx/notification/pop.mp3`,
    duration: 0.5,
    audioType: 'sfx',
    attribution: { artist: 'SFX Library', source: 'Royalty Free' },
  },
  {
    id: 'sfx-notification-003',
    name: 'Chime',
    type: 'audio',
    tags: ['sfx', 'benachrichtigung', 'glocke', 'melodisch', 'elegant'],
    category: 'Benachrichtigung',
    audioUrl: `${AUDIO_BASE_URL}/sfx/notification/chime.mp3`,
    duration: 2,
    audioType: 'sfx',
    attribution: { artist: 'SFX Library', source: 'Royalty Free' },
  },
  {
    id: 'sfx-notification-004',
    name: 'Digital Beep',
    type: 'audio',
    tags: ['sfx', 'benachrichtigung', 'digital', 'beep', 'tech'],
    category: 'Benachrichtigung',
    audioUrl: `${AUDIO_BASE_URL}/sfx/notification/digital-beep.mp3`,
    duration: 0.8,
    audioType: 'sfx',
    attribution: { artist: 'Digital SFX', source: 'Royalty Free' },
  },
];

/**
 * Sound Effects: UI/Klicks
 */
const uiSfx: AudioElement[] = [
  {
    id: 'sfx-ui-001',
    name: 'Button Click',
    type: 'audio',
    tags: ['sfx', 'klick', 'button', 'ui', 'interface'],
    category: 'UI',
    audioUrl: `${AUDIO_BASE_URL}/sfx/ui/button-click.mp3`,
    duration: 0.2,
    audioType: 'sfx',
    attribution: { artist: 'UI Sounds', source: 'Royalty Free' },
  },
  {
    id: 'sfx-ui-002',
    name: 'Toggle Switch',
    type: 'audio',
    tags: ['sfx', 'toggle', 'schalter', 'switch', 'ui'],
    category: 'UI',
    audioUrl: `${AUDIO_BASE_URL}/sfx/ui/toggle-switch.mp3`,
    duration: 0.3,
    audioType: 'sfx',
    attribution: { artist: 'UI Sounds', source: 'Royalty Free' },
  },
  {
    id: 'sfx-ui-003',
    name: 'Menu Open',
    type: 'audio',
    tags: ['sfx', 'menü', 'öffnen', 'slide', 'navigation'],
    category: 'UI',
    audioUrl: `${AUDIO_BASE_URL}/sfx/ui/menu-open.mp3`,
    duration: 0.4,
    audioType: 'sfx',
    attribution: { artist: 'UI Sounds', source: 'Royalty Free' },
  },
  {
    id: 'sfx-ui-004',
    name: 'Typing',
    type: 'audio',
    tags: ['sfx', 'tippen', 'tastatur', 'keyboard', 'text'],
    category: 'UI',
    audioUrl: `${AUDIO_BASE_URL}/sfx/ui/typing.mp3`,
    duration: 0.15,
    audioType: 'sfx',
    attribution: { artist: 'UI Sounds', source: 'Royalty Free' },
  },
];

/**
 * Sound Effects: Bewegung/Whoosh
 */
const whooshSfx: AudioElement[] = [
  {
    id: 'sfx-whoosh-001',
    name: 'Whoosh Fast',
    type: 'audio',
    tags: ['sfx', 'whoosh', 'schnell', 'bewegung', 'transition'],
    category: 'Whoosh',
    audioUrl: `${AUDIO_BASE_URL}/sfx/whoosh/whoosh-fast.mp3`,
    duration: 0.5,
    audioType: 'sfx',
    attribution: { artist: 'Motion SFX', source: 'Royalty Free' },
  },
  {
    id: 'sfx-whoosh-002',
    name: 'Whoosh Soft',
    type: 'audio',
    tags: ['sfx', 'whoosh', 'sanft', 'luft', 'smooth'],
    category: 'Whoosh',
    audioUrl: `${AUDIO_BASE_URL}/sfx/whoosh/whoosh-soft.mp3`,
    duration: 0.7,
    audioType: 'sfx',
    attribution: { artist: 'Motion SFX', source: 'Royalty Free' },
  },
  {
    id: 'sfx-whoosh-003',
    name: 'Swipe',
    type: 'audio',
    tags: ['sfx', 'swipe', 'wischen', 'slide', 'übergang'],
    category: 'Whoosh',
    audioUrl: `${AUDIO_BASE_URL}/sfx/whoosh/swipe.mp3`,
    duration: 0.4,
    audioType: 'sfx',
    attribution: { artist: 'Motion SFX', source: 'Royalty Free' },
  },
  {
    id: 'sfx-whoosh-004',
    name: 'Cinematic Swoosh',
    type: 'audio',
    tags: ['sfx', 'swoosh', 'cinematic', 'dramatisch', 'film'],
    category: 'Whoosh',
    audioUrl: `${AUDIO_BASE_URL}/sfx/whoosh/cinematic-swoosh.mp3`,
    duration: 1,
    audioType: 'sfx',
    attribution: { artist: 'Film SFX', source: 'Royalty Free' },
  },
];

/**
 * Sound Effects: Erfolg/Feedback
 */
const feedbackSfx: AudioElement[] = [
  {
    id: 'sfx-success-001',
    name: 'Success Chime',
    type: 'audio',
    tags: ['sfx', 'erfolg', 'abgeschlossen', 'positiv', 'fertig'],
    category: 'Feedback',
    audioUrl: `${AUDIO_BASE_URL}/sfx/feedback/success-chime.mp3`,
    duration: 1.5,
    audioType: 'sfx',
    attribution: { artist: 'Feedback SFX', source: 'Royalty Free' },
  },
  {
    id: 'sfx-success-002',
    name: 'Achievement',
    type: 'audio',
    tags: ['sfx', 'achievement', 'gewonnen', 'belohnung', 'level-up'],
    category: 'Feedback',
    audioUrl: `${AUDIO_BASE_URL}/sfx/feedback/achievement.mp3`,
    duration: 2,
    audioType: 'sfx',
    attribution: { artist: 'Game SFX', source: 'Royalty Free' },
  },
  {
    id: 'sfx-error-001',
    name: 'Error Buzz',
    type: 'audio',
    tags: ['sfx', 'fehler', 'error', 'negativ', 'warnung'],
    category: 'Feedback',
    audioUrl: `${AUDIO_BASE_URL}/sfx/feedback/error-buzz.mp3`,
    duration: 0.8,
    audioType: 'sfx',
    attribution: { artist: 'Feedback SFX', source: 'Royalty Free' },
  },
  {
    id: 'sfx-error-002',
    name: 'Denied',
    type: 'audio',
    tags: ['sfx', 'abgelehnt', 'denied', 'falsch', 'nein'],
    category: 'Feedback',
    audioUrl: `${AUDIO_BASE_URL}/sfx/feedback/denied.mp3`,
    duration: 0.6,
    audioType: 'sfx',
    attribution: { artist: 'Feedback SFX', source: 'Royalty Free' },
  },
];

/**
 * Ambient: Natur
 */
const natureAmbient: AudioElement[] = [
  {
    id: 'ambient-nature-001',
    name: 'Wald Atmosphäre',
    type: 'audio',
    tags: ['ambient', 'natur', 'wald', 'vögel', 'bäume'],
    category: 'Natur',
    audioUrl: `${AUDIO_BASE_URL}/ambient/nature/forest-atmosphere.mp3`,
    duration: 300,
    audioType: 'ambient',
    attribution: { artist: 'Nature Records', source: 'Royalty Free' },
  },
  {
    id: 'ambient-nature-002',
    name: 'Regen',
    type: 'audio',
    tags: ['ambient', 'natur', 'regen', 'entspannung', 'schlaf'],
    category: 'Natur',
    audioUrl: `${AUDIO_BASE_URL}/ambient/nature/rain.mp3`,
    duration: 600,
    audioType: 'ambient',
    attribution: { artist: 'Nature Records', source: 'Royalty Free' },
  },
  {
    id: 'ambient-nature-003',
    name: 'Strand & Wellen',
    type: 'audio',
    tags: ['ambient', 'natur', 'strand', 'wellen', 'meer', 'ozean'],
    category: 'Natur',
    audioUrl: `${AUDIO_BASE_URL}/ambient/nature/beach-waves.mp3`,
    duration: 360,
    audioType: 'ambient',
    attribution: { artist: 'Ocean Sounds', source: 'Royalty Free' },
  },
  {
    id: 'ambient-nature-004',
    name: 'Gewitter',
    type: 'audio',
    tags: ['ambient', 'natur', 'gewitter', 'donner', 'sturm'],
    category: 'Natur',
    audioUrl: `${AUDIO_BASE_URL}/ambient/nature/thunderstorm.mp3`,
    duration: 420,
    audioType: 'ambient',
    attribution: { artist: 'Storm Chasers', source: 'Royalty Free' },
  },
  {
    id: 'ambient-nature-005',
    name: 'Lagerfeuer',
    type: 'audio',
    tags: ['ambient', 'natur', 'feuer', 'knistern', 'camping'],
    category: 'Natur',
    audioUrl: `${AUDIO_BASE_URL}/ambient/nature/campfire.mp3`,
    duration: 480,
    audioType: 'ambient',
    attribution: { artist: 'Outdoor Audio', source: 'Royalty Free' },
  },
];

/**
 * Ambient: Stadt
 */
const cityAmbient: AudioElement[] = [
  {
    id: 'ambient-city-001',
    name: 'Straßenverkehr',
    type: 'audio',
    tags: ['ambient', 'stadt', 'verkehr', 'autos', 'urban'],
    category: 'Stadt',
    audioUrl: `${AUDIO_BASE_URL}/ambient/city/street-traffic.mp3`,
    duration: 300,
    audioType: 'ambient',
    attribution: { artist: 'Urban Sounds', source: 'Royalty Free' },
  },
  {
    id: 'ambient-city-002',
    name: 'Café Atmosphäre',
    type: 'audio',
    tags: ['ambient', 'stadt', 'café', 'gespräche', 'gemütlich'],
    category: 'Stadt',
    audioUrl: `${AUDIO_BASE_URL}/ambient/city/cafe-atmosphere.mp3`,
    duration: 360,
    audioType: 'ambient',
    attribution: { artist: 'Urban Sounds', source: 'Royalty Free' },
  },
  {
    id: 'ambient-city-003',
    name: 'U-Bahn Station',
    type: 'audio',
    tags: ['ambient', 'stadt', 'metro', 'zug', 'untergrund'],
    category: 'Stadt',
    audioUrl: `${AUDIO_BASE_URL}/ambient/city/subway-station.mp3`,
    duration: 240,
    audioType: 'ambient',
    attribution: { artist: 'Transit Audio', source: 'Royalty Free' },
  },
  {
    id: 'ambient-city-004',
    name: 'Nachtclub',
    type: 'audio',
    tags: ['ambient', 'stadt', 'club', 'bass', 'nachtleben'],
    category: 'Stadt',
    audioUrl: `${AUDIO_BASE_URL}/ambient/city/nightclub.mp3`,
    duration: 180,
    audioType: 'ambient',
    attribution: { artist: 'Club Sound', source: 'Royalty Free' },
  },
];

/**
 * Ambient: Büro/Arbeit
 */
const officeAmbient: AudioElement[] = [
  {
    id: 'ambient-office-001',
    name: 'Büro Atmosphäre',
    type: 'audio',
    tags: ['ambient', 'büro', 'arbeit', 'tastatur', 'fokus'],
    category: 'Büro',
    audioUrl: `${AUDIO_BASE_URL}/ambient/office/office-atmosphere.mp3`,
    duration: 300,
    audioType: 'ambient',
    attribution: { artist: 'Work Sounds', source: 'Royalty Free' },
  },
  {
    id: 'ambient-office-002',
    name: 'Konferenzraum',
    type: 'audio',
    tags: ['ambient', 'büro', 'meeting', 'diskussion', 'business'],
    category: 'Büro',
    audioUrl: `${AUDIO_BASE_URL}/ambient/office/conference-room.mp3`,
    duration: 240,
    audioType: 'ambient',
    attribution: { artist: 'Work Sounds', source: 'Royalty Free' },
  },
  {
    id: 'ambient-office-003',
    name: 'Coworking Space',
    type: 'audio',
    tags: ['ambient', 'büro', 'coworking', 'startup', 'kreativ'],
    category: 'Büro',
    audioUrl: `${AUDIO_BASE_URL}/ambient/office/coworking-space.mp3`,
    duration: 360,
    audioType: 'ambient',
    attribution: { artist: 'Startup Audio', source: 'Royalty Free' },
  },
  {
    id: 'ambient-office-004',
    name: 'Server Room',
    type: 'audio',
    tags: ['ambient', 'büro', 'server', 'technik', 'lüfter', 'digital'],
    category: 'Büro',
    audioUrl: `${AUDIO_BASE_URL}/ambient/office/server-room.mp3`,
    duration: 180,
    audioType: 'ambient',
    attribution: { artist: 'Tech Sounds', source: 'Royalty Free' },
  },
];

/**
 * Alle Audio-Elemente zusammengeführt
 */
export const AUDIO_LIBRARY: AudioElement[] = [
  // Musik
  ...upbeatMusic,
  ...chillMusic,
  ...corporateMusic,
  ...cinematicMusic,
  // SFX
  ...notificationSfx,
  ...uiSfx,
  ...whooshSfx,
  ...feedbackSfx,
  // Ambient
  ...natureAmbient,
  ...cityAmbient,
  ...officeAmbient,
];

/**
 * Audio nach ID abrufen
 */
export function getAudioById(id: string): AudioElement | undefined {
  return AUDIO_LIBRARY.find((audio) => audio.id === id);
}

/**
 * Audio nach Typ filtern
 */
export function getAudioByType(
  audioType: AudioElement['audioType']
): AudioElement[] {
  return AUDIO_LIBRARY.filter((audio) => audio.audioType === audioType);
}

/**
 * Audio nach Kategorie filtern
 */
export function getAudioByCategory(category: string): AudioElement[] {
  return AUDIO_LIBRARY.filter((audio) => audio.category === category);
}

/**
 * Audio nach Tags suchen
 */
export function searchAudio(query: string): AudioElement[] {
  const lowerQuery = query.toLowerCase();
  return AUDIO_LIBRARY.filter(
    (audio) =>
      audio.name.toLowerCase().includes(lowerQuery) ||
      audio.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)) ||
      (audio.category && audio.category.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Verfügbare Audio-Typen
 */
export const AUDIO_TYPES = ['music', 'sfx', 'ambient'] as const;
export type AudioType = (typeof AUDIO_TYPES)[number];

/**
 * Audio-Typ Labels für die UI
 */
export const AUDIO_TYPE_LABELS: Record<AudioType, string> = {
  music: 'Musik',
  sfx: 'Soundeffekte',
  ambient: 'Atmosphäre',
};

/**
 * Alle verfügbaren Kategorien
 */
export const AUDIO_CATEGORIES = {
  music: ['Upbeat', 'Chill', 'Corporate', 'Cinematic'],
  sfx: ['Benachrichtigung', 'UI', 'Whoosh', 'Feedback'],
  ambient: ['Natur', 'Stadt', 'Büro'],
} as const;

/**
 * Hilfsfunktion: Dauer formatieren (mm:ss)
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Hilfsfunktion: Kurze Dauer für SFX (0.5s, 1.2s etc.)
 */
export function formatShortDuration(seconds: number): string {
  if (seconds < 1) {
    return `${(seconds * 1000).toFixed(0)}ms`;
  }
  if (seconds < 10) {
    return `${seconds.toFixed(1)}s`;
  }
  return formatDuration(seconds);
}
